import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(4, 120, 87, 0.78),
    rgba(255, 107, 107, 0.4)
  );
  overflow: hidden;
  color: white;
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: linear-gradient(
    135deg,
    rgba(4, 120, 87, 0.92) 0%,
    rgba(16, 185, 129, 0.7) 25%,
    rgba(255, 107, 107, 0.2) 50%,
    rgba(16, 185, 129, 0.7) 75%,
    rgba(4, 120, 87, 0.92) 100%
  );
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;

  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("/assets/2025/08/portage-cubes.png") center/cover;
    opacity: 0.1;
    animation: float 6s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-20px) scale(1.05);
    }
  }
`;

const VideoBackground = styled.iframe`
  position: absolute;
  top: -60px;
  left: -60px;
  width: calc(100% + 120px);
  height: calc(100% + 120px);
  object-fit: cover;
  z-index: -2;
  pointer-events: none;
  border: none;
  overflow: hidden;
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  z-index: 2;
`;

const HeroTitle = styled.div`
  margin-bottom: 3rem;

  h1 {
    font-family: "Inter", sans-serif;
    font-size: 5rem;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 1rem;
    animation: ${fadeIn} 1s ease forwards;
    letter-spacing: -0.02em;
    color: white;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .highlight {
    color: var(--secondary-color);
    display: inline-block;
    animation: ${fadeIn} 1s ease 0.3s both;
  }

  .subtitle {
    margin-top: -20px;
    animation: ${fadeIn} 1s ease 0.6s both;
  }
`;

const HeroDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.4rem;
  line-height: 1.7;
  margin-bottom: 3rem;
  color: #f1f5f9;
  animation: ${fadeIn} 1s ease 0.9s both;
  font-weight: 400;

  strong {
    color: var(--secondary-color);
    font-weight: 600;
  }
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease 1.2s both;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;

  .rating {
    display: flex;
    gap: 4px;
    margin-bottom: 8px;
    justify-content: center;

    .star {
      color: #ffd700;
      font-size: 1.2rem;
    }
  }

  .rating-text {
    font-size: 0.9rem;
    color: #b8c5d1;
  }
`;

const ClientCount = styled.div`
  text-align: center;

  .count {
    font-size: 2rem;
    font-weight: 700;
    color: var(--secondary-color);
    font-family: "Inter", sans-serif;
    display: block;
  }

  .label {
    font-size: 0.9rem;
    color: #cbd5e1;
    font-family: "Inter", sans-serif;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease 1.2s both;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    font-family: "Inter", sans-serif;
    font-weight: 600;
    font-size: 1.1rem;
    padding: 16px 32px;
    border-radius: 12px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: none;
    cursor: pointer;
  }

  .btn-primary {
    background: linear-gradient(
      135deg,
      var(--secondary-color) 0%,
      var(--secondary-dark) 100%
    );
    color: white;
    box-shadow: 0 10px 25px rgba(255, 107, 107, 0.3);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 35px rgba(255, 107, 107, 0.4);
    }
  }

  .btn-secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: var(--secondary-color);
      transform: translateY(-2px);
    }
  }
`;

const HeroVisual = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  animation: ${fadeIn} 1s ease 1.5s both;

  @media (max-width: 1024px) {
    order: -1;
  }
`;

const TestimonialImages = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 80px);
  gap: 10px;
  margin-bottom: 2rem;

  img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid var(--secondary-color);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 60px);

    img {
      width: 60px;
      height: 60px;
    }
  }
`;

const HeroImage = styled.div`
  position: relative;

  img {
    max-width: 100%;
    height: auto;

    opacity: 1;
  }
`;

const CircularText = styled.div`
  position: absolute;
  top: -50px;
  right: -50px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: rgba(222, 185, 122, 0.1);
  border: 2px solid var(--secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  color: var(--secondary-color);
  animation: rotate 20s linear infinite;

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    top: -30px;
    right: -30px;
    font-size: 0.7rem;
  }
`;

const Hero = () => {
  return (
    <HeroSection>
      <AnimatedBackground />
      <VideoBackground
        src="https://www.youtube.com/embed/KEFt2quibkg?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1&playlist=KEFt2quibkg&cc_load_policy=0&fs=0&color=white&disablekb=1&start=0&end=0"
        title="Container Background Video"
        frameBorder="0"
        allow="autoplay; encrypted-media"
      />

      <HeroContainer>
        <HeroContent>
          <HeroTitle>
            <h1>
              Tailored <span className="highlight">CONTAINER</span>
            </h1>
            <h1 className="subtitle">Solutions</h1>
          </HeroTitle>

          <HeroDescription>
            At Baypoint Storage, we make it simple to buy high-quality{" "}
            <strong>new and used shipping containers</strong> that fit your
            storage, construction, or custom project needs.
          </HeroDescription>

          <HeroStats>
            <StatItem>
              <div className="rating">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="star">
                    ★
                  </span>
                ))}
              </div>
              <div className="rating-text">(4.9/5)</div>
            </StatItem>

            <ClientCount>
              <span className="count">2K+</span>
              <span className="label">Happy Clients</span>
            </ClientCount>
          </HeroStats>

          <HeroActions>
            <Link to="/containers" className="btn btn-primary">
              Browse Containers
              <i className="fas fa-arrow-right"></i>
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              Get Quote
              <i className="fas fa-phone"></i>
            </Link>
          </HeroActions>
        </HeroContent>

        <HeroVisual>
          <TestimonialImages>
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
              alt="Customer testimonial"
            />
            <img src="/assets/2025/08/Testi2.jpg" alt="Customer testimonial" />
            <img
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
              alt="Customer testimonial"
            />
            <img
              src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
              alt="Customer testimonial"
            />
          </TestimonialImages>

          <HeroImage>
            <img
              src="/assets/2025/08/background_removed_image_tiVmqctBTz6JexX8XSNL6A_075715.png"
              alt="Baypoint Storage Containers"
            />
            <CircularText>Beyond Delivery – We Build Partnerships</CircularText>
          </HeroImage>
        </HeroVisual>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero;
